import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { apiFetch, setTokenGetter } from '../lib/api';

function summarizeActionResult(action: string, data: Record<string, number | string | boolean>): string {
  if (action === 'scout') {
    const inserted = typeof data.inserted === 'number' ? data.inserted : 0;
    const updated = typeof data.updated === 'number' ? data.updated : 0;
    const rejected = typeof data.rejected === 'number' ? data.rejected : 0;
    return `${inserted} new, ${updated} updated, ${rejected} rejected`;
  }
  if (action === 'navigator') {
    const promoted = typeof data.promoted === 'number' ? data.promoted : 0;
    return promoted > 0 ? `${promoted} promoted to draft` : 'No candidates ready';
  }
  if (action === 'quartermaster') {
    const enriched = typeof data.enriched === 'number' ? data.enriched : 0;
    return enriched > 0 ? `${enriched} enriched` : 'No drafts to enrich';
  }
  if (action === 'comms') {
    const triaged = typeof data.triaged === 'number' ? data.triaged : 0;
    const escalated = typeof data.escalated === 'number' ? data.escalated : 0;
    return triaged > 0 ? `${triaged} triaged, ${escalated} escalated` : 'No messages';
  }
  return 'Complete';
}

export interface Bot {
  id: string;
  name: string;
  room: string;
  status: 'idle' | 'working' | 'awaiting_approval' | 'alert' | 'paused';
  last_action_at: string | null;
}

export interface Listing {
  id: number;
  title: string;
  category: string | null;
  status: string;
  priceCents: number;
  costCents: number;
  estProfitCents: number;
  profitMargin: number;
  navigatorScore: number;
  keywords: string[];
  imageUrl: string | null;
  updatedAt: string;
}

export interface BuyerMessage {
  id: number;
  buyer: string;
  subject: string | null;
  body: string;
  intent: string | null;
  sentiment: string | null;
  draftedReply: string | null;
  replyStatus: string;
  receivedAt: string;
}

export interface BudgetGuardrails {
  minMarginPct: number;
  minProfitUsd: number;
  maxSupplierCostUsd: number;
  maxListingPriceUsd: number;
  usOnly: boolean;
}

export interface BudgetSnapshot {
  buildBudgetUsd: number;
  month0TargetUsd: number;
  ongoingTargetUsd: number;
  estimatedBuildSpendUsd: number;
  estimatedMonth0SpendUsd: number;
  remainingBuildUsd: number;
  remainingMonth0Usd: number;
  posture: 'green' | 'yellow' | 'red';
  notes: string[];
  guardrails: BudgetGuardrails;
}

export interface Approval {
  id: number;
  botId: string;
  kind: string;
  payload: unknown;
  status: 'pending' | 'approved' | 'rejected' | 'expired';
  decisionNote: string | null;
  createdAt: string;
  decidedAt: string | null;
}

interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  username: string | null;
  pendingChallenge: string | null;
  bots: Bot[];
  approvals: Approval[];
  listings: Listing[];
  messages: BuyerMessage[];
  budget: BudgetSnapshot;
  error: string | null;
  loading: boolean;
  actionBusy: string | null;
  toast: string | null;

  login: (username: string, password: string) => Promise<void>;
  submitTotp: (token: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshAll: () => Promise<void>;
  decideApproval: (id: number, decision: 'approve' | 'reject', note?: string) => Promise<void>;
  triggerAction: (action: 'scout' | 'navigator' | 'quartermaster' | 'comms') => Promise<void>;
  clearError: () => void;
}

export const useAppStore = create<AuthState>()(
  persist(
    (set, get) => ({
      accessToken: null,
      refreshToken: null,
      username: null,
      pendingChallenge: null,
      bots: [],
      approvals: [],
      listings: [],
      messages: [],
      budget: {
        buildBudgetUsd: 450,
        month0TargetUsd: 522,
        ongoingTargetUsd: 150,
        estimatedBuildSpendUsd: 270,
        estimatedMonth0SpendUsd: 342,
        remainingBuildUsd: 180,
        remainingMonth0Usd: 180,
        posture: 'green',
        notes: [
          'No VPS created yet',
          'No paid supplier tools yet',
          'Most recent work has been code, not new cash burn',
        ],
        guardrails: {
          minMarginPct: 20,
          minProfitUsd: 12,
          maxSupplierCostUsd: 30,
          maxListingPriceUsd: 75,
          usOnly: true,
        },
      },
      error: null,
      loading: false,
      actionBusy: null,
      toast: null,

      async login(username, password) {
        set({ loading: true, error: null });
        try {
          const res = await apiFetch('/api/auth/login', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ username, password }),
          });
          const data = await res.json();
          if (!res.ok) throw new Error(data.error ?? 'Login failed');
          if (data.requiresTotp) {
            set({ pendingChallenge: data.challenge, username, loading: false });
            return;
          }
          set({
            accessToken: data.accessToken,
            refreshToken: data.refreshToken,
            username,
            pendingChallenge: null,
            loading: false,
          });
          await get().refreshAll();
        } catch (err) {
          set({ error: (err as Error).message, loading: false });
        }
      },

      async submitTotp(token) {
        const challenge = get().pendingChallenge;
        if (!challenge) {
          set({ error: 'No pending 2FA challenge' });
          return;
        }
        set({ loading: true, error: null });
        try {
          const res = await apiFetch('/api/auth/login/totp', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ challenge, token }),
          });
          const data = await res.json();
          if (!res.ok) throw new Error(data.error ?? '2FA failed');
          set({
            accessToken: data.accessToken,
            refreshToken: data.refreshToken,
            pendingChallenge: null,
            loading: false,
          });
          await get().refreshAll();
        } catch (err) {
          set({ error: (err as Error).message, loading: false });
        }
      },

      async logout() {
        const rt = get().refreshToken;
        if (rt) {
          try {
            await apiFetch('/api/auth/logout', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ refreshToken: rt }),
            });
          } catch {
            // best-effort
          }
        }
        set({ accessToken: null, refreshToken: null, username: null, bots: [], approvals: [], listings: [], messages: [], pendingChallenge: null });
      },

      async refreshAll() {
        const token = get().accessToken;
        if (!token) return;
        try {
          const [botsRes, apprRes, listingsRes, messagesRes] = await Promise.all([
            apiFetch('/api/bots'),
            apiFetch('/api/approvals?status=pending'),
            apiFetch('/api/listings?limit=50'),
            apiFetch('/api/messages?limit=50'),
          ]);
          if (botsRes.ok) { const d = await botsRes.json(); set({ bots: d.bots ?? [] }); }
          if (apprRes.ok) { const d = await apprRes.json(); set({ approvals: d.approvals ?? [] }); }
          if (listingsRes.ok) { const d = await listingsRes.json(); set({ listings: d.listings ?? [] }); }
          if (messagesRes.ok) { const d = await messagesRes.json(); set({ messages: d.messages ?? [] }); }
        } catch (err) {
          console.error('refreshAll failed', err);
        }
      },

      async triggerAction(action) {
        const endpoints: Record<typeof action, string> = {
          scout: '/api/candidates/scan',
          navigator: '/api/listings/promote',
          quartermaster: '/api/listings/enrich',
          comms: '/api/messages/triage',
        };
        const labels: Record<typeof action, string> = {
          scout: 'Scout',
          navigator: 'Navigator',
          quartermaster: 'Quartermaster',
          comms: 'Comms',
        };
        set({ actionBusy: action, toast: null });
        try {
          const res = await apiFetch(endpoints[action], { method: 'POST' });
          const data = await res.json().catch(() => ({} as Record<string, unknown>));
          if (!res.ok) throw new Error((data as { error?: string }).error ?? 'Action failed');
          const summary = summarizeActionResult(action, data as Record<string, number | string | boolean>);
          set({ toast: `${labels[action]}: ${summary}` });
          setTimeout(() => set((state) => state.toast?.startsWith(labels[action]) ? { toast: null } : {}), 5000);
          await get().refreshAll();
        } catch (err) {
          set({ error: (err as Error).message, toast: `${labels[action]} failed: ${(err as Error).message}` });
          setTimeout(() => set({ toast: null }), 5000);
        } finally {
          set({ actionBusy: null });
        }
      },

      async decideApproval(id, decision, note) {
        try {
          const res = await apiFetch(`/api/approvals/${id}/decide`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ decision, note }),
          });
          if (!res.ok) {
            const data = await res.json().catch(() => ({}));
            throw new Error(data.error ?? 'Decision failed');
          }
          await get().refreshAll();
        } catch (err) {
          set({ error: (err as Error).message });
        }
      },

      clearError() {
        set({ error: null });
      },
    }),
    {
      name: 'horizon-auth',
      partialize: (s) => ({ accessToken: s.accessToken, refreshToken: s.refreshToken, username: s.username }),
    }
  )
);

// Wire token getter for apiFetch (avoids circular import)
setTokenGetter(() => useAppStore.getState().accessToken);
